import { BookOpenTextIcon, CalendarCheckIcon, CalendarXIcon, ClipboardCheckIcon, FileTextIcon, LayersIcon, LayoutGridIcon, MapIcon, SnowflakeIcon, UsersIcon, TimerIcon } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
export const BRAND = 'LIIKUNTA VALMIS™';
export const heroGuarantees = ['Välitön digitaalinen lataus', 'Kertamaksu', 'Ei tilausta', '7 päivän tyytyväisyystakuu'];
export const stats = [{
  value: '240',
  label: 'Sivua pääosissa'
}, {
  value: '6',
  label: 'Digitaalista osaa'
}, {
  value: '9',
  label: 'Luokka-astetta – 1. luokalta 9. luokalle'
}, {
  value: '5',
  label: 'Bonusta mukana'
}, {
  value: '3',
  label: 'Tasoa jokaisessa harjoitteessa'
}];
export const differenceStats = [{
  value: '1.–9. luokka',
  label: 'Alakoulusta yläkouluun'
}, {
  value: '6 digitaalista osaa',
  label: 'Leikeistä ja pallopeleistä talviliikuntaan, uintiin, fyysiseen toimintakykyyn ja arviointiin'
}, {
  value: 'Tulostettavat tuntikortit',
  label: 'Käytä tabletilla tai tulosta mukaan liikuntasaliin'
}];
export const beforeItems: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Illat ja viikonloput katoavat',
  text: 'Suunnittelet ensi viikon liikuntatunteja ruudun ääressä – ja silti tunti alkaa usein improvisoinnilla.'
}, {
  title: 'Jokainen tunti suunnitellaan tyhjästä',
  text: 'Lämmittely, pääosa, loppuleikki, välineet ja ryhmäjaot – joka ikiselle tunnille erikseen.'
}, {
  title: '25 oppilasta, hyvin eri tasoilla',
  text: 'Osa liikkuu harrastuksissa joka päivä, osa jännittää jo pallon kiinniottoa – ja tunnin pitäisi toimia kaikille.'
}, {
  title: 'Salikartat ja järjestelyt päässä',
  text: 'Missä asemat ovat, mihin kartiot tulevat ja miten ryhmä kiertää? Kaikki pitää miettiä ennen kuin oppilaat tulevat saliin.'
}, {
  title: 'Arviointi on hankalaa perustella',
  text: 'Arviointikriteerit, havainnointi, itsearviointi ja numerot 4–10 – miten arvioit liikuntaa reilusti ja dokumentoit sen?'
}, {
  title: 'Talviliikunta ja uinti vaativat oman suunnittelunsa',
  text: 'Hiihto, luistelu, suunnistus, uimataito ja turvallisuus – ja sää muuttaa suunnitelmat viime hetkellä.'
}];
export const afterItems = ['Valmis vuosisuunnitelma 1.–2., 3.–6. ja 7.–9. luokille', '12 valmista liikuntatuntia minuutti minuutilta', 'Alkulämmittely, pääosa, loppuleikki ja välineet jokaiselle tunnille', 'Salikartat ylhäältä katsottuna', 'Tulostettavat tuntikortit', 'Jokainen harjoite kolmella tasolla', 'Linkitetty POPS 2014:n liikunnan tavoitteisiin', 'Talviliikunta, ulkoliikunta ja uinti', 'Taitotestit, arviointimatriisit ja 100 valmista palautekommenttia'];
export const recognizeItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: CalendarXIcon,
  title: 'Illat kuluvat suunnitteluun',
  text: 'Käytätkö iltoja ja viikonloppuja ensi viikon liikuntatuntien suunnitteluun?'
}, {
  icon: TimerIcon,
  title: 'Tunti alkaa ennen kuin olet valmis',
  text: 'Kävelet saliin ja mietit vielä, mitä lämmittelyssä tehdään ja mitä välineitä tarvitaan?'
}, {
  icon: UsersIcon,
  title: 'Ryhmässä on suuret tasoerot',
  text: 'Tarvitsetko harjoitteita, jotka toimivat sekä aloittelijoille että urheilua harrastaville?'
}, {
  icon: MapIcon,
  title: 'Järjestelyt vievät tunnista aikaa',
  text: 'Kuluuko tunnista liikaa aikaa asemien rakentamiseen, ryhmäjakoihin ja ohjeiden selittämiseen?'
}, {
  icon: ClipboardCheckIcon,
  title: 'Arviointi tuntuu epämääräiseltä',
  text: 'Haluaisitko valmiit taitotestit, kriteerit ja palautekommentit, joilla arviointi on reilua ja perusteltua?'
}, {
  icon: SnowflakeIcon,
  title: 'Talvi ja uinti tuovat lisätyötä',
  text: 'Kaipaatko valmiita suunnitelmia hiihtoon, luisteluun, suunnistukseen ja uimaopetukseen?'
}];

// Spine colours mirror the physical box (blue, light blue, green, yellow, orange, red).
export const volumes: Array<{
  number: string;
  level: string;
  title: string;
  tagline: string;
  intro: string;
  items: string[];
  outro: string;
  note?: string;
  spine: string;
  rule: string;
}> = [{
  number: 'Osa 01',
  level: '1.–9. luokka',
  title: 'Vuosisuunnitelma ja 12 valmista liikuntatuntia',
  tagline: 'Tiedät tarkalleen, mitä jokaisella tunnilla tehdään – koko lukuvuoden.',
  intro: 'Ensimmäinen osa antaa sinulle valmiit vuosisuunnitelmat kaikille vuosiluokille sekä kaksitoista liikuntatuntia, jotka voit pitää heti.',
  items: ['Vuosisuunnitelmat 1.–2., 3.–6. ja 7.–9. luokille', '38 kouluviikkoa POPS-tavoitteisiin linkitettynä', '12 valmista tuntia minuutti minuutilta', '45 minuutin tunnit', 'Alkulämmittely, pääosa, loppuleikki ja välineet', 'Opettajan ohje jokaiselle tunnille', 'Salikartat ylhäältä katsottuna', 'Tulostettavat tuntikortit kolmella tasolla', 'Taitotesti ja arviointimatriisi'],
  outro: 'Valmiit tunnit, joiden ansiosta menet liikuntasaliin selkeän suunnitelman kanssa.',
  spine: 'bg-blue-700 text-white',
  rule: 'border-white/30'
}, {
  number: 'Osa 02',
  level: '1.–2. luokka',
  title: 'Leikit, motoriset perustaidot ja liikkumisen ilo',
  tagline: 'Vahva perusta liikkumiselle – leikin kautta.',
  intro: 'Toinen osa rakentaa motoriset perustaidot leikkien, ratojen ja asemien avulla – niin, että jokainen pienimmistäkin pääsee onnistumaan.',
  items: ['Tasapainotaidot', 'Liikkumistaidot', 'Välineenkäsittelytaidot', 'Leikit ja hippaleikit', 'Rata- ja asemaharjoittelu', 'Vesileikit ja vesitaitojen alkeet', 'Harjoitteet kolmella tasolla', 'Salikartat ja tuntikortit'],
  outro: 'Toiminnallista liikuntaa, jossa ilo ja oppiminen kulkevat yhdessä.',
  spine: 'bg-sky-500 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Osa 03',
  level: '3.–6. luokka',
  title: 'Pallopelit, yhteistyö ja reilu peli',
  tagline: 'Pelit, joissa jokainen pääsee mukaan.',
  intro: 'Kolmas osa vie oppilaat pallopelien perustaidoista sääntöihin, rooleihin ja reiluun peliin – selkeällä progressiolla.',
  items: ['Jalkapallo', 'Salibandy', 'Koripallo', 'Lentopallo', 'Pesäpallo', 'Säännöt ja roolit', 'Reilu peli ja yhteistyö', 'Progressio helposta haastavaan', 'Harjoitteet kolmella tasolla'],
  outro: 'Selkeät pelitunnit, joissa jokainen oppilas on mukana – ei vain parhaat pelaajat.',
  spine: 'bg-green-600 text-white',
  rule: 'border-white/30'
}, {
  number: 'Osa 04',
  level: '1.–9. luokka',
  title: 'Ulkoliikunta, talviliikunta ja uinti',
  tagline: 'Suomalainen liikuntavuosi – lumesta uimahalliin.',
  intro: 'Neljäs osa kattaa ulkoliikunnan, talviliikunnan ja uinnin turvallisesti ja valmiiksi suunniteltuna.',
  items: ['Hiihto', 'Luistelu', 'Suunnistus', 'Retkeily', 'Uimataito ja vesipelastus', 'Turvallisuus ulkona ja vedessä', 'Pukeutuminen säähän ja jokamiehenoikeudet', 'Harjoitteet kolmella tasolla'],
  outro: 'Valmiit suunnitelmat niihin tunteihin, jotka yleensä vievät eniten valmisteluaikaa.',
  spine: 'bg-yellow-400 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Osa 05',
  level: '7.–9. luokka',
  title: 'Fyysinen toimintakyky ja hyvinvointi',
  tagline: 'Yläkoululaiset ymmärtämään omaa kehoaan ja harjoitteluaan.',
  intro: 'Viides osa opettaa harjoittelun periaatteet ja auttaa oppilaita rakentamaan oman harjoitusohjelman.',
  items: ['Kestävyys, voima, nopeus ja liikkuvuus', 'Harjoittelun periaatteet', 'Oma harjoitusohjelma', 'Move!-mittausten tuki', 'Uni ja palautuminen', 'Itsearviointi', 'Harjoitteet kolmella tasolla'],
  outro: 'Valmiit tunnit, joiden avulla hyvinvointi muuttuu teoriasta teoiksi.',
  spine: 'bg-orange-500 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Osa 06',
  level: '1.–9. luokka',
  title: 'Arviointi ja palaute',
  tagline: 'Reilu, perusteltu ja selkeä liikunnan arviointi.',
  intro: 'Kuudes osa kokoaa arvioinnin työkalut: kriteerit, havainnoinnin, itse- ja vertaisarvioinnin sekä valmiit palautteet.',
  items: ['Arviointikriteerit 6. ja 9. luokalle (5, 7, 8 ja 9)', 'Havainnointilomakkeet', 'Itse- ja vertaisarviointi', 'Taitotestit', 'Teoriatehtävät A/B vastauksineen', '100 valmista palautekommenttia'],
  outro: 'Kattava tuki oikeudenmukaiseen arviointiin – ilman iltoja paperien parissa.',
  note: 'Materiaali on itsenäisesti laadittu, eikä se ole Opetushallituksen virallinen julkaisu.',
  spine: 'bg-red-600 text-white',
  rule: 'border-white/30'
}];
export const volumesIntro = {
  title: '6 digitaalista osaa liikuntaan',
  text: 'Kokonainen opetusjärjestelmä, joka auttaa suunnittelemaan ja toteuttamaan liikunnanopetuksen koko lukuvuoden ajan. Jokaisessa osassa on 40 sivua valmiita tunteja, salikarttoja, tuntikortteja ja harjoitteita kolmella tasolla.'
};
export const packItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: BookOpenTextIcon,
  title: 'Valmiit tunnit minuutti minuutilta',
  text: 'Ei enää tyhjää paperia. Alkulämmittely, pääosa, loppuleikki, välineet ja opettajan ohje – jokainen 45 minuutin tunti valmiina.'
}, {
  icon: LayoutGridIcon,
  title: 'Salikartat ja tuntikortit',
  text: 'Näet heti, mihin asemat ja välineet tulevat. Tulosta tuntikortti mukaan saliin tai pidä se tabletilla.'
}, {
  icon: FileTextIcon,
  title: 'Kolme tasoa',
  text: 'Sama harjoite kolmella tasolla, jotta jokainen oppilas – aloittelijasta harrastajaan – pääsee onnistumaan.'
}, {
  icon: LayersIcon,
  title: 'POPS 2014:n mukainen',
  text: 'Liikunnan tavoitteet vuosiluokille 1–2, 3–6 ja 7–9: fyysinen, sosiaalinen ja psyykkinen toimintakyky.'
}, {
  icon: ClipboardCheckIcon,
  title: 'Arviointi valmiina',
  text: 'Taitotestit, arviointimatriisit, kriteerit 6. ja 9. luokalle sekä 100 valmista palautekommenttia.'
}, {
  icon: CalendarCheckIcon,
  title: 'Vuosisuunnitelma',
  text: '38 kouluviikkoa jäsenneltynä – talviliikunta, uinti ja ulkoliikunta oikeissa kohdissa lukuvuotta.'
}];
// Real pages from Osa 1 (Gamma Estudio; WebP 1512x1890 in Impultienda Recursos, store c4cac064-...).
const PAGE_BASE = 'https://aekpgsbqjzqntlbucbts.supabase.co/storage/v1/object/public/store-resources/c4cac064-0d81-4ed5-8138-a30200b5bbb9/';
export const pageSamples: Array<{
  src: string;
  label: string;
  title: string;
  text: string;
}> = [{
  src: `${PAGE_BASE}1792635354210_lv-osa1-sivu01-f6180b61.webp`,
  label: 'Osa 1 · Kansilehti',
  title: 'Vuosisuunnitelma + 12 valmista tuntia minuutti minuutilta',
  text: 'Osa 1 kattaa vuosiluokat 1.–2., 3.–6. ja 7.–9. POPS 2014:n mukaan.'
}, {
  src: `${PAGE_BASE}1792635358167_lv-osa1-sivu12-78eed9a5.webp`,
  label: 'Osa 1 · Sivu 12',
  title: 'Tunti 1 · 1.–2. lk · 45 min · Metsän eläimet',
  text: 'Valmis tunti: tavoite, välineet, kulku minuutti minuutilta, salikartta ja mitä opettaja sanoo.'
}, {
  src: `${PAGE_BASE}1792635361792_lv-osa1-sivu13-ffaf0f06.webp`,
  label: 'Osa 1 · Sivu 13',
  title: 'Tuntikortti · Tunti 1 · Metsän eläimet',
  text: 'Koko tunti yhdellä kortilla: aikataulu, asemien kierto, kolme tasoa ja vinkkisanat.'
}, {
  src: `${PAGE_BASE}1792635365481_lv-osa1-sivu36-0b5568f6.webp`,
  label: 'Osa 1 · Sivu 36',
  title: 'Taitotesti · 1.–6. luokka',
  text: 'Kahdeksan asemaa ja selkeät pisteytysohjeet – tulosta ja käytä heti.'
}, {
  src: `${PAGE_BASE}1792635369156_lv-osa1-sivu39-ce365795.webp`,
  label: 'Osa 1 · Sivu 39',
  title: 'Arviointimatriisi',
  text: 'Liikkumistaidot, yhteistyö, yrittäminen, reiluus ja oman toiminnan arviointi neljällä tasolla – ja valmiit kommentit.'
}];
export const pageTypes = ['Valmiit tunnit', 'Salikartat', 'Tuntikortit', 'Taitotestit', 'Arviointi'];
export const bonuses: Array<{
  title: string;
  text: string;
  items: string[];
  benefit: string;
}> = [{
  title: 'Sijaispaketti – 12 valmista liikuntatuntia sijaiselle',
  text: 'Kaksitoista valmista tuntia, jotka sijainen voi pitää ilman valmistelua – myös silloin, kun sali ei ole käytössä.',
  items: ['12 valmista 45 minuutin tuntia', '1.–2., 3.–6. ja 7.–9. luokille', 'Salikartat ja säännöt', 'Ohje sijaiselle'],
  benefit: 'Ei hukattuja tunteja, kun olet poissa.'
}, {
  title: '5 minuutin liikuntatauot – 180 taukoliikuntahetkeä luokkaan',
  text: 'Lyhyet liikuntatauot luokkahuoneeseen: aktivoivia, rauhoittavia, tasapaino- ja yhteistyöhetkiä.',
  items: ['180 taukoliikuntahetkeä', 'Riittää koko lukuvuodeksi', '5 minuuttia kerrallaan', 'Kesto, taso ja välineet jokaisessa'],
  benefit: 'Enemmän liikettä koulupäivään – ilman lisäsuunnittelua.'
}, {
  title: 'Kuvatuki liikuntasaliin – tulostettavat kuvakortit ja asemakortit',
  text: 'Selkeät kuvakortit tunnin kulkuun, signaaleihin, sääntöihin ja asemille – iso kuva, vähän tekstiä.',
  items: ['Asemakortit kolmella tasolla', 'Tunnin kulku ja signaalit', 'Salin säännöt kuvina', 'Itsearviointikortit'],
  benefit: 'Vähemmän selittämistä – enemmän liikkumista.'
}, {
  title: 'Liikuntapäivä vaihe vaiheelta – koulun liikunta- ja talviliikuntapäivä',
  text: 'Valmis suunnitelma koko koulun liikuntapäivään ja talviliikuntapäivään aikatauluineen ja rasteineen.',
  items: ['Suunnittelun aikajana', 'Rastit ja aikataulut', 'Talviliikuntapäivän vaihtoehdot', 'Turvallisuus ja tiedotus'],
  benefit: 'Liikuntapäivä hoituu ilman viime hetken kiirettä.'
}, {
  title: 'Koti–koulu-viestintä – valmiit Wilma-viestit ja kirjeet huoltajille',
  text: 'Valmiit viestipohjat huoltajille: varusteet, talviliikunta, uinti, retket ja palaute.',
  items: ['Valmiit Wilma-viestit', 'Kirjeet huoltajille', 'Varusteet ja talviliikunta', 'Uinti ja retket'],
  benefit: 'Selkeä viestintä kotiin – muutamassa sekunnissa.'
}];
export const steps: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Avaa LIIKUNTA VALMIS™',
  text: 'Lataa paketti ja avaa luokka-asteellesi sopiva osa.'
}, {
  title: 'Valitse luokka ja tunti',
  text: 'Löydä ryhmällesi sopiva tunti, laji tai tuntikortti.'
}, {
  title: 'Valmistaudu 5 minuutissa',
  text: 'Lue opettajan ohje, katso salikartta ja ota välineet esille.'
}, {
  title: 'Mene liikuntasaliin',
  text: 'Tulosta tuntikortti tai pidä se tabletilla – ja aloita tunti selkeän suunnitelman kanssa.'
}];
export const audienceItems = ['Opetat liikuntaa 1.–9. luokalla', 'Olet luokanopettaja ja lukujärjestyksessäsi on liikuntaa', 'Olet liikunnanopettaja ja haluat säästää suunnitteluaikaa', 'Olet erityisopettaja ja ohjaat liikuntaa', 'Haluat valmiin vuosisuunnitelman POPS 2014:n mukaan', 'Opetat ryhmiä, joissa on suuret tasoerot', 'Haluat tukea liikunnan arviointiin', 'Haluat säästää iltasi ja viikonloppusi'];
export const timeValueTasks = ['Harjoitteiden ja leikkien etsiminen netistä', 'Tuntien ja salikarttojen suunnittelu', 'Harjoitteiden porrastaminen eri tasoille', 'Talviliikunnan ja uinnin järjestelyt', 'Arviointi ja palautteen antaminen'];
export const offerRows: Array<{
  label: string;
  detail: string;
}> = [{
  label: '6 digitaalista osaa – 240 sivua',
  detail: 'Vuosisuunnitelma ja valmiit tunnit, leikit ja perustaidot, pallopelit, ulko- ja talviliikunta sekä uinti, fyysinen toimintakyky ja arviointi'
}, {
  label: '5 bonusta ilman lisämaksua',
  detail: 'Sijaispaketti, 5 minuutin liikuntatauot, Kuvatuki liikuntasaliin, Liikuntapäivä vaihe vaiheelta ja Koti–koulu-viestintä'
}, {
  label: '1.–9. luokille',
  detail: 'Valmiit tunnit, salikartat, tuntikortit, kolme tasoa ja arviointi'
}];
export const offerIncludes = ['Välitön digitaalinen lataus', 'Tallennettavat PDF-tiedostot', 'Tulostusvalmis', 'Fyysistä tuotetta ei lähetetä'];
export const PRICE = '25 €';
export const PRICE_BEFORE = '55 €';

// Only add authentic reviews of LIIKUNTA VALMIS. The section stays hidden while empty.
export const testimonials: Array<{
  quote: string;
  name: string;
  location: string;
}> = [];
export const faqs = [{
  q: 'Miten saan materiaalin?',
  a: 'LIIKUNTA VALMIS™ toimitetaan digitaalisesti. Oston jälkeen saat käyttöösi PDF-tiedostot, jotka voit ladata ja tallentaa. Voit käyttää materiaalia tietokoneella, tabletilla tai puhelimella ja tulostaa tarvitsemasi sivut.'
}, {
  q: 'Onko materiaali POPS 2014:n mukainen?',
  a: 'Kyllä. Vuosisuunnitelmat ja tunnit on linkitetty perusopetuksen opetussuunnitelman perusteiden (POPS 2014) liikunnan tavoitteisiin vuosiluokille 1–2, 3–6 ja 7–9: fyysinen, sosiaalinen ja psyykkinen toimintakyky. LIIKUNTA VALMIS™ on itsenäisesti laadittu opetusmateriaali, eikä se ole Opetushallituksen virallinen julkaisu.'
}, {
  q: 'Mille luokille materiaali sopii?',
  a: 'Materiaali kattaa luokat 1–9. Osa 2 on 1.–2. luokille, osa 3 on 3.–6. luokille ja osa 5 on 7.–9. luokille. Osat 1, 4 ja 6 sisältävät materiaalia kaikille vuosiluokille. Valitset tunnit oman ryhmäsi mukaan.'
}, {
  q: 'Voinko todella valmistella tunnin 5 minuutissa?',
  a: 'LIIKUNTA VALMIS™ on tehty nopeuttamaan valmistelua. Avaat osan, valitset valmiin tunnin, katsot salikartan ja luet opettajan ohjeen noin viidessä minuutissa. Aika voi vaihdella tunnin, välineiden ja ryhmän mukaan.'
}, {
  q: 'Riittääkö materiaali koko lukuvuodeksi?',
  a: 'LIIKUNTA VALMIS™ sisältää vuosisuunnitelmat 38 kouluviikolle sekä valmiit tunnit ja kokonaisuudet kaikissa osissa. Valitset, yhdistelet ja muokkaat materiaalia oman suunnitelmasi, salivuorojesi ja oppilaidesi mukaan.'
}, {
  q: 'Toimiiko materiaali, jos ryhmässä on suuret tasoerot?',
  a: 'Kyllä. Harjoitteet ja tuntikortit on porrastettu kolmelle tasolle, joten sama tunti toimii sekä aloittelijoille että paljon liikkuville oppilaille.'
}, {
  q: 'Sisältyykö talviliikunta ja uinti?',
  a: 'Kyllä. Osa 4 sisältää hiihdon, luistelun, suunnistuksen, retkeilyn sekä uimataidon ja vesipelastuksen – turvallisuusohjeineen.'
}, {
  q: 'Sisältyykö arviointi?',
  a: 'Kyllä. Osa 6 sisältää arviointikriteerit 6. ja 9. luokalle (arvosanat 5, 7, 8 ja 9), havainnointilomakkeet, itse- ja vertaisarvioinnin, taitotestit, teoriatehtävät A/B vastauksineen ja 100 valmista palautekommenttia.'
}, {
  q: 'Vaikuttavatko Move!-mittaukset arvosanaan?',
  a: 'Eivät. Move! on fyysisen toimintakyvyn seurantajärjestelmä, jonka mittaukset tehdään 5. ja 8. luokalla, eikä tuloksia käytetä liikunnan arvosanan perusteena. Osa 5 auttaa valmistelemaan mittaukset ja käsittelemään tuloksia oppilaan oman hyvinvoinnin näkökulmasta.'
}, {
  q: 'Pitääkö kaikki tulostaa?',
  a: 'Ei. Valitset itse, mitkä sivut tulostat. Tuntikortit toimivat myös tabletilta liikuntasalissa.'
}, {
  q: 'Sisältyvätkö viisi bonusta hintaan?',
  a: `Kyllä. Kaikki viisi bonusta sisältyvät pakettiin ilman lisämaksua – kaikki yhteensä ${PRICE}.`
}, {
  q: 'Onko kyseessä tilaus?',
  a: 'Ei. Maksat kerran ja saat digitaalisen materiaalin käyttöösi. Ei toistuvia veloituksia.'
}, {
  q: 'Voinko käyttää materiaalia puhelimella tai tabletilla?',
  a: 'Kyllä. PDF-tiedostot aukeavat tietokoneella, tabletilla ja puhelimella. Salikarttojen tarkasteluun suosittelemme tablettia tai tulostettua tuntikorttia.'
}, {
  q: 'Onko tuotteella takuu?',
  a: 'Kyllä. LIIKUNTA VALMIS™ -paketilla on 7 päivän tyytyväisyystakuu. Jos materiaali ei vastaa odotuksiasi, voit ottaa meihin yhteyttä ja pyytää rahat takaisin takuuajan sisällä ehtojen mukaisesti.'
}];
export const footerLinks = ['Ostoehdot', 'Tietosuoja', 'Palautukset', 'Yhteystiedot'];
